/**
 * W-8EXP Service
 * Following the same clean architecture pattern as W8BEN, W8ECI, and W9 services
 */

import { W8EXPFormModel, W8EXPResponseModel, W8EXPFieldMapper } from "../Models/W8EXPModels";
import { BASE_URL } from "../App_Settings/app_settings";
import { getUserIdFromLocalStorage } from "../API_Endpoints/APIEndpoints";
import { parseAddressComponents, detectTINType, determineStateFieldType } from "../utils/formUtils";
import { updateFileData } from "./fileDataService";

export class W8EXPService {
  private static readonly API_ENDPOINT = `${BASE_URL}/FileData/ImportFiles`;

  /**
   * Create form model from API response data
   * Note: API only sends selected checkboxes, unselected ones are not included
   */
  static fromApiResponse(apiData: any): W8EXPFormModel {
    // Look specifically for territory government related fields
    const territoryKeys = Object.keys(apiData).filter(
      (key) =>
        key.toLowerCase().includes("territory") ||
        key.toLowerCase().includes("government") ||
        key.toLowerCase().includes("u.s.")
    );

    // Create response model from API data
    const responseModel = new W8EXPResponseModel(apiData);

    // Map legacy API field names and convert boolean values
    this.mapLegacyApiFields(apiData, responseModel);

    // Use field mapper to convert to form model
    const formModel = W8EXPFieldMapper.mapResponseToForm(responseModel);

    // Handle address parsing for combined address fields
    this.parseAddressFields(apiData, formModel);

    // Handle checkbox behavior: API only includes selected checkboxes
    // Any checkbox field not present in API data should be false
    // Always handle checkbox defaults for initial API response processing
    this.handleCheckboxDefaults(formModel, apiData);

    return formModel;
  }

  /**
   * Parse combined address fields from API data
   */
  private static parseAddressFields(apiData: any, formModel: W8EXPFormModel): void {
    // Only parse addresses if mapping is not done (prevents overwriting user changes)
    if (formModel.isMapDone) {
      // console.log("🛡️ W8EXPService: Skipping address parsing - isMapDone is true");
      return;
    }

    // Parse permanent address
    if (apiData["EXP-Permanent_address_city_state_postal"]) {
      const addressComponents = parseAddressComponents(
        apiData["EXP-Permanent_address_city_state_postal"]
      );
      formModel["Permanent address city"] = addressComponents.city;

      // Use utility function to determine which state field to use
      const country =
        apiData["EXP-Permanent_address_country"] ||
        apiData["Permanent_address_country"] ||
        formModel["Permanent address country"] ||
        "";
      const stateFieldType = determineStateFieldType(country, addressComponents.state);

      if (stateFieldType === "USCA") {
        formModel["Permanent address state USCA"] = addressComponents.state;
        formModel["Permanent address state NonUS"] = "";
      } else {
        formModel["Permanent address state NonUS"] = addressComponents.state;
        formModel["Permanent address state USCA"] = "";
      }

      formModel["Permanent address zip"] = addressComponents.postalCode;
    }

    // Parse mailing address
    if (apiData["EXP-Mailing_address_city_state_postal"]) {
      const addressComponents = parseAddressComponents(
        apiData["EXP-Mailing_address_city_state_postal"]
      );
      formModel["Mailing city address"] = addressComponents.city;

      // Use utility function to determine which state field to use
      const country =
        apiData["EXP-Mailing_address_country"] ||
        apiData["Mailing_address_country"] ||
        formModel["Mailing address country"] ||
        "";
      const stateFieldType = determineStateFieldType(country, addressComponents.state);

      if (stateFieldType === "USCA") {
        formModel["Mailing address state USCA"] = addressComponents.state;
        formModel["Mailing address state NonUS"] = "";
      } else {
        formModel["Mailing address state NonUS"] = addressComponents.state;
        formModel["Mailing address state USCA"] = "";
      }

      formModel["Mailing address zip"] = addressComponents.postalCode;
    }
  }

  /**
   * Handle checkbox defaults - if a checkbox field isn't in API data, it should be false
   */
  private static handleCheckboxDefaults(formModel: W8EXPFormModel, apiData: any): void {
    const checkboxFields = [
      // Chapter 3 Status checkboxes
      "EXP-Foreign_central_bank_issue",
      "EXP-Foreign_government",
      "EXP-Foreign_private_foundation",
      "EXP-Foreign_tax-exempt_organization",
      "EXP-Government_of_us_territory",
      "EXP-International_organization",
      "EXP-Withholding_qualified_holder",

      // Chapter 4 FATCA Status checkboxes
      "EXP-501c_organization",
      "EXP-Direct_reporting_NFFE",
      "EXP-Exempt_retirement_plan",
      "EXP-4_foreign_government_central_bank",
      "EXP-4_international_organization",
      "EXP-Nonreporting_IGA_FFI",
      "EXP-Participating_FFI",
      "EXP-Passive_NFFE",
      "EXP-Registered_deemed_compliant_FFI",
      "EXP-Reporting_Model1_FFI",
      "EXP-Reporting_Model2_FFI",
      "EXP-Sponsored_direct_reporting_NFFE",
      "EXP-Territory_financial_institution",

      // Part II and III checkboxes
      "EXP-10A_Foreign_government",
      "EXP-10B_Integral_part",
      "EXP-10C_Controlled_entity",
      "EXP-10D_Not_commercial_activities",
      "EXP-10E_Central_bank_not_us_commercial",
      "EXP-11_International_organization",
      "EXP-12_Foreign_central_bank",
      "EXP-13A_Irs_determination",
      "EXP-13B_Us_counsel_opinion",
      "EXP-13C_Not_private_foundation",
      "EXP-13D_Is_private_foundation",
      "EXP-14_Us_territory_government",
      "EXP-15A_Qualified_holder",
      "EXP-15B_Foreign_partnership_qualified_holders",
      "EXP-16_Nonreporting_IGA_FFI",
      "EXP-17_Territory_financial_institution",
      "EXP-18_Foreign_government_fatca",
      "EXP-19A_Retirement_plan_employees",
      "EXP-19B_Entity_identitfier",
      "EXP-20_501c_Organization_fatca",
      "EXP-21A_Passive_nffe_certification",
      "EXP-21B_No_substantial_us_owners",
      "EXP-21C_Has_substantial_us_owners",
      "EXP-22_Sponsored_direct_reporting",

      // Other checkboxes
      "GIINCheckBox",
      "GIIN Applied For",
      "Foreign TIN",
    ];

    checkboxFields.forEach((field) => {
      // Get the API field name (either direct or mapped)
      let apiFieldName = field;

      // If the field is not present in API data, set it to false
      if (!(field in apiData) && !(apiFieldName in apiData)) {
        (formModel as any)[field] = false;
      } else {
        // If it is present, convert to boolean properly
        const value = apiData[field] || apiData[apiFieldName];
        const boolValue = this.convertToBoolean(value);
        (formModel as any)[field] = boolValue;
      }
    });

    // Auto-check GIIN checkbox if GIIN field has a value, otherwise set to false
    const giinValue = apiData["GIIN"];
    if (giinValue && giinValue.toString().trim()) {
      (formModel as any)["GIINCheckBox"] = true;
    } else {
      (formModel as any)["GIINCheckBox"] = false;
      console.log(`Setting GIIN checkbox to false because GIIN field is empty or null`);
    }

    // Auto-check Foreign TIN checkbox if Foreign tax identifying number has a value, otherwise set to false
    const foreignTinValue = apiData["Foreign tax identifying number"];
    if (foreignTinValue && foreignTinValue.toString().trim()) {
      (formModel as any)["Foreign TIN"] = true;
      console.log(
        `Auto-checking Foreign TIN checkbox because Foreign tax identifying number field has value: ${foreignTinValue}`
      );
    } else {
      (formModel as any)["Foreign TIN"] = false;
      console.log(
        `Setting Foreign TIN checkbox to false because Foreign tax identifying number field is empty or null`
      );
    }
  }

  /**
   * Parse address components from combined address string
   */
  private static parseAddressComponents(address: string): {
    city: string;
    state: string;
    postalCode: string;
  } {
    const result = { city: "", state: "", postalCode: "" };

    if (!address) return result;

    const parts = address.split(",").map((part) => part.trim());

    if (parts.length >= 1) result.city = parts[0];
    if (parts.length >= 2) result.state = parts[1];
    if (parts.length >= 3) result.postalCode = parts[2];

    return result;
  }

  /**
   * Convert various value types to boolean for checkboxes
   */
  private static convertToBoolean(value: any): boolean {
    if (typeof value === "boolean") return value;
    if (typeof value === "string") {
      return (
        value.toLowerCase() === "true" ||
        value === "1" ||
        value.toLowerCase() === "yes" ||
        value.toLowerCase() === "selected"
      );
    }
    if (typeof value === "number") return value === 1;
    return !!value;
  }

  /**
   * Map legacy API field names to current field names
   */
  private static mapLegacyApiFields(apiData: any, responseModel: W8EXPResponseModel): void {
    // Check if mapping is already done - if so, skip address parsing to prevent overwriting user changes
    if (apiData.isMapDone || responseModel.isMapDone) {
      // console.log("Skipping address parsing - isMapDone is true");
      return;
    }
    const legacyMappings: Record<string, string[]> = {
      "Name of organization": [
        "Organization_name",
        "organization_name",
        "Name",
        "name",
        "entity_name",
      ],

      "Date of birth": ["Date_of_birth", "date_of_birth", "Date of birth"],
      "Permanent residence address": [
        "Permanent_residence_address",
        "permanent_address",
        "address_line_1",
        "Permanent residence address",
      ],

      "Permanent address country": [
        "Permanent_address_country",
        "permanent_country",
        "country",
        "Permanent address country",
      ],
      "Mailing address": ["Mailing_address", "mailing_address", "Mailing address"],

      "U.S. taxpayer identification number": [
        "US_taxpayer_identification_number",
        "us_tin",
        "U.S._taxpayer_identification_number",
      ],
      GIIN: ["GIIN", "giin", "global_intermediary_identification_number"],
      "Foreign tax identifying number": [
        "Foreign_tax_identifying_number",
        "foreign_tin",
        "Foreign_tax_identifying_number",
      ],
      "Taxpayer type": ["Taxpayer_type", "taxpayer_type", "Taxpayer type", "identification_type"],
      "EXP-Reference_numbers": ["EXP-Reference_numbers", "reference_numbers", "Reference_numbers"],
      GIINCheckBox: ["GIINCheckBox", "giin_checkbox", "giin_check"],
      "GIIN Applied For": ["GIIN Applied For", "giin_applied_for", "giin_applied"],
      "Foreign TIN": ["Foreign TIN", "foreign_tin_checkbox", "foreign_tin_check"],

      // Address field mappings - detailed
      "Permanent address line 2": ["Permanent_address_line_2", "permanent_address_line_2"],
      "Permanent address line 3": ["Permanent_address_line_3", "permanent_address_line_3"],
      "Permanent address city": [
        "Permanent_address_city",
        "permanent_address_city",
        "permanent_city",
        "EXP-Permanent_address_city_state_postal",
      ],
      "Permanent address state USCA": [
        "Permanent_address_state_USCA",
        "permanent_address_state_usca",
      ],
      "Permanent address state NonUS": [
        "Permanent_address_state_NonUS",
        "permanent_address_state_nonus",
        "EXP-Permanent_address_city_state_postal",
      ],
      "Permanent address zip": ["Permanent_address_zip", "permanent_address_zip"],
      "Permanent address other": ["Permanent_address_other", "permanent_address_other"],
      "Permanent address is care of": [
        "Permanent_address_is_care_of",
        "permanent_address_is_care_of",
      ],
      "Permanent address is po box": ["Permanent_address_is_po_box", "permanent_address_is_po_box"],
      "Permanent address is rural route": [
        "Permanent_address_is_rural_route",
        "permanent_address_is_rural_route",
      ],

      "Mailing address line 2": ["Mailing_address_line_2", "mailing_address_line_2"],
      "Mailing address line 3": ["Mailing_address_line_3", "mailing_address_line_3"],
      "Mailing city address": [
        "Mailing_city_address",
        "mailing_city_address",
        "mailing_city",
        "EXP-Mailing_address_city_state_postal",
      ],
      "Mailing address state USCA": ["Mailing_address_state_USCA", "mailing_address_state_usca"],
      "Mailing address state NonUS": [
        "Mailing_address_state_NonUS",
        "mailing_address_state_nonus",
        "EXP-Mailing_address_city_state_postal",
      ],
      "Mailing address zip": ["Mailing_address_zip", "mailing_address_zip"],
      "Mailing address other": ["Mailing_address_other", "mailing_address_other"],
      "Mailing address is care of": ["Mailing_address_is_care_of", "mailing_address_is_care_of"],
      "Mailing address is po box": ["Mailing_address_is_po_box", "mailing_address_is_po_box"],
      "Mailing address is rural route": [
        "Mailing_address_is_rural_route",
        "mailing_address_is_rural_route",
      ],

      // Section 13 fields - with correct API field names
      "EXP-13A_Text": [
        "EXP-13A_Text",
        "exp_13a_text",
        "13a_text",
        "EXP-13A_tax_exempt_determination_letter",
      ],

      "EXP-13B_Text": ["EXP-13B_Text", "exp_13b_text", "13b_text", "EXP-13B_us_counsel_opinion"],
      "EXP-13B_Date": [
        "EXP-13B_Date",
        "exp_13b_date",
        "13b_date",
        "EXP-13B_us_counsel_opinion_date",
      ],

      Signature_date: ["Signature_date"],
      // Form field variant
      Print_name: ["Print_name", "printed_name", "signer_name", "Signer_printed_name"],
      Signing_capacity: ["Signing_capacity", "signing_capacity", "capacity"],
      isSigned: ["isSigned", "is_signed", "signed"],
      isModified: ["isModified", "is_modified", "modified"],
      Form_name: ["Form_name", "form_name", "form_type"],
      Form_revision: ["Form_revision", "form_revision", "revision_date"],

      // Entity Classification fields (Section 4)
      "EXP-Foreign_government": ["EXP-Foreign_government", "foreign_government"],
      "EXP-International_organization": [
        "EXP-International_organization",
        "international_organization",
      ],
      "EXP-Foreign_central_bank_issue": ["EXP-Foreign_central_bank_issue", "foreign_central_bank"],
      "EXP-Foreign_tax-exempt_organization": [
        "EXP-Foreign_tax-exempt_organization",
        "tax_exempt_organization",
      ],
      "EXP-Foreign_private_foundation": ["EXP-Foreign_private_foundation", "private_foundation"],
      "EXP-Government_of_us_territory": [
        "EXP-Government_of_us_territory",
        "EXP-Government_of_us_territory", // API uses lowercase 'us' without dots
        "us_territory_government",
      ],
      "EXP-Withholding_qualified_holder": ["EXP-Withholding_qualified_holder", "qualified_holder"],

      // FATCA Classification fields (Section 5)
      "EXP-Participating_FFI": ["EXP-Participating_FFI", "participating_ffi"],
      "EXP-Reporting_Model1_FFI": ["EXP-Reporting_Model1_FFI", "model1_ffi"],
      "EXP-Reporting_Model2_FFI": ["EXP-Reporting_Model2_FFI", "model2_ffi"],
      "EXP-Registered_deemed_compliant_FFI": [
        "EXP-Registered_deemed_compliant_FFI",
        "deemed_compliant_ffi",
      ],
      "EXP-Nonreporting_IGA_FFI": ["EXP-Nonreporting_IGA_FFI", "nonreporting_iga_ffi"],
      "EXP-Territory_financial_institution": [
        "EXP-Territory_financial_institution",
        "territory_financial",
      ],
      "EXP-4_international_organization": [
        "EXP-4_International_organization",
        "international_org_4",
      ],
      "EXP-4_foreign_government_central_bank": [
        "EXP-4_Foreign_government_central_bank",
        "foreign_govt_bank_4",
      ],
      "EXP-Exempt_retirement_plan": ["EXP-Exempt_retirement_plan", "exempt_retirement"],
      "EXP-501c_organization": ["EXP-501c_organization"],
      "EXP-Passive_NFFE": ["EXP-Passive_NFFE", "passive_nffe"],
      "EXP-Direct_reporting_NFFE": ["EXP-Direct_reporting_NFFE", "direct_reporting_nffe"],
      "EXP-Sponsored_direct_reporting_NFFE": [
        "EXP-Sponsored_direct_reporting_NFFE",
        "sponsored_direct_reporting",
      ],
    };

    Object.entries(legacyMappings).forEach(([modelField, legacyFields]) => {
      if (!responseModel[modelField]) {
        for (const legacyField of legacyFields) {
          if (apiData[legacyField] !== undefined) {
            responseModel[modelField] = apiData[legacyField];
            break;
          }
        }
      }
    });

    // Handle combined address field from API
    if (apiData["EXP-Permanent_address_city_state_postal"]) {
      const combinedAddress = apiData["EXP-Permanent_address_city_state_postal"];
      const addressParts = this.parseAddressComponents(combinedAddress);

      responseModel["Permanent address city"] = addressParts.city;

      // Use utility function to determine which state field to use
      const country =
        apiData["EXP-Permanent_address_country"] ||
        apiData["Permanent_address_country"] ||
        responseModel["Permanent address country"] ||
        "";
      const stateFieldType = determineStateFieldType(country, addressParts.state);

      if (stateFieldType === "USCA") {
        responseModel["Permanent address state USCA"] = addressParts.state;
        responseModel["Permanent address state NonUS"] = "";
      } else {
        responseModel["Permanent address state NonUS"] = addressParts.state;
        responseModel["Permanent address state USCA"] = "";
      }

      responseModel["Permanent address zip"] = addressParts.postalCode;
    }

    // Handle combined mailing address field from API
    if (apiData["EXP-Mailing_address_city_state_postal"]) {
      const combinedAddress = apiData["EXP-Mailing_address_city_state_postal"];
      console.log("Found mailing address combined field:", combinedAddress);
      const addressParts = this.parseAddressComponents(combinedAddress);

      responseModel["Mailing city address"] = addressParts.city;

      // Use utility function to determine which state field to use
      const country =
        apiData["EXP-Mailing_address_country"] ||
        apiData["Mailing_address_country"] ||
        responseModel["Mailing address country"] ||
        "";
      const stateFieldType = determineStateFieldType(country, addressParts.state);

      if (stateFieldType === "USCA") {
        responseModel["Mailing address state USCA"] = addressParts.state;
        responseModel["Mailing address state NonUS"] = "";
      } else {
        responseModel["Mailing address state NonUS"] = addressParts.state;
        responseModel["Mailing address state USCA"] = "";
      }

      responseModel["Mailing address zip"] = addressParts.postalCode;
    }

    // Auto-detect taxpayer type from TIN if not set
    if (!responseModel["Taxpayer type"]) {
      const usTin = responseModel["U.S. taxpayer identification number"];
      const foreignTin = responseModel["Foreign tax identifying number"];

      // Check which TIN field has a value and auto-detect type
      const activeTin =
        usTin && usTin.toString().trim()
          ? usTin.toString()
          : foreignTin && foreignTin.toString().trim()
            ? foreignTin.toString()
            : null;

      if (activeTin) {
        const detectedType = detectTINType(activeTin);
        if (
          detectedType &&
          (detectedType === "EIN" ||
            detectedType === "SSN/ITIN" ||
            detectedType === "SSN" ||
            detectedType === "ITIN")
        ) {
          const mappedType =
            detectedType === "SSN" || detectedType === "ITIN" ? "SSN/ITIN" : detectedType;
          responseModel["Taxpayer type"] = mappedType;
        }
      }
    }

    // Convert API boolean-like values to actual booleans/strings for specific fields
    const booleanFields = [
      "EXP-10A_Foreign_government",
      "EXP-10B_Integral_part",
      "EXP-10C_Controlled_entity",
      "EXP-10D_Not_commercial_activities",
      "EXP-10E_Central_bank_not_us_commercial",
      "EXP-11_International_organization",
      "EXP-12_Foreign_central_bank",
      "EXP-13A_Irs_determination",
      "EXP-13B_Us_counsel_opinion",
      "EXP-13C_Not_private_foundation",
      "EXP-13D_Is_private_foundation",
      "EXP-14_Us_territory_government",
      "EXP-15A_Qualified_holder",
      "EXP-15B_Foreign_partnership_qualified_holders",
      "EXP-16_Nonreporting_IGA_FFI",
      "EXP-17_Territory_financial_institution",
      "EXP-18_Foreign_government_fatca",
      "EXP-19A_Retirement_plan_employees",
      "EXP-19B_Entity_identitfier",
      "EXP-20_501c_Organization_fatca",
      "EXP-21A_Passive_nffe_certification",
      "EXP-21B_No_substantial_us_owners",
      "EXP-21C_Has_substantial_us_owners",
      "EXP-22_Sponsored_direct_reporting",
      "EXP-4_foreign_government_central_bank",
      "EXP-4_international_organization",
      "EXP-501c_organization",
      "EXP-Direct_reporting_NFFE",
      "EXP-Exempt_retirement_plan",
      "EXP-Foreign_central_bank_issue",
      "EXP-Foreign_government",
      "EXP-Foreign_private_foundation",
      "EXP-Foreign_tax-exempt_organization",
      "EXP-Government_of_us_territory",
      "EXP-International_organization",
      "EXP-Nonreporting_IGA_FFI",
      "EXP-Participating_FFI",
      "EXP-Passive_NFFE",
      "EXP-Registered_deemed_compliant_FFI",
      "EXP-Reporting_Model1_FFI",
      "EXP-Reporting_Model2_FFI",
      "EXP-Sponsored_direct_reporting_NFFE",
      "EXP-Territory_financial_institution",
      "EXP-Withholding_qualified_holder",
    ];

    booleanFields.forEach((field) => {
      if (responseModel[field] !== undefined) {
        const value = responseModel[field];
        // Convert string values to boolean
        if (typeof value === "string") {
          responseModel[field] =
            value.toLowerCase() === "true" || value === "1" || value.toLowerCase() === "yes";
        } else if (typeof value === "number") {
          responseModel[field] = value === 1;
        }
      }
    });
  }

  /**
   * Convert form model to API payload format
   * Note: Only send checkbox fields that are true, omit false values
   */
  static toApiPayload(model: W8EXPFormModel): Record<string, any> {
    const payload: Record<string, any> = {};

    const checkboxFields = [
      // Chapter 3 Status checkboxes
      "EXP-Foreign_central_bank_issue",
      "EXP-Foreign_government",
      "EXP-Foreign_private_foundation",
      "EXP-Foreign_tax-exempt_organization",
      "EXP-Government_of_us_territory",
      "EXP-International_organization",
      "EXP-Withholding_qualified_holder",

      // Chapter 4 FATCA Status checkboxes
      "EXP-501c_organization",
      "EXP-Direct_reporting_NFFE",
      "EXP-Exempt_retirement_plan",
      "EXP-4_foreign_government_central_bank",
      "EXP-4_international_organization",
      "EXP-Nonreporting_IGA_FFI",
      "EXP-Participating_FFI",
      "EXP-Passive_NFFE",
      "EXP-Registered_deemed_compliant_FFI",
      "EXP-Reporting_Model1_FFI",
      "EXP-Reporting_Model2_FFI",
      "EXP-Sponsored_direct_reporting_NFFE",
      "EXP-Territory_financial_institution",

      // Part II and III checkboxes
      "EXP-10A_Foreign_government",
      "EXP-10B_Integral_part",
      "EXP-10C_Controlled_entity",
      "EXP-10D_Not_commercial_activities",
      "EXP-10E_Central_bank_not_us_commercial",
      "EXP-11_International_organization",
      "EXP-12_Foreign_central_bank",
      "EXP-13A_Irs_determination",
      "EXP-13B_Us_counsel_opinion",
      "EXP-13C_Not_private_foundation",
      "EXP-13D_Is_private_foundation",
      "EXP-14_Us_territory_government",
      "EXP-15A_Qualified_holder",
      "EXP-15B_Foreign_partnership_qualified_holders",
      "EXP-16_Nonreporting_IGA_FFI",
      "EXP-17_Territory_financial_institution",
      "EXP-18_Foreign_government_fatca",
      "EXP-19A_Retirement_plan_employees",
      "EXP-19B_Entity_identitfier",
      "EXP-20_501c_Organization_fatca",
      "EXP-21A_Passive_nffe_certification",
      "EXP-21B_No_substantial_us_owners",
      "EXP-21C_Has_substantial_us_owners",
      "EXP-22_Sponsored_direct_reporting",

      // Other checkboxes
      "GIINCheckBox",
      "GIIN Applied For",
      "Foreign TIN",
    ];

    // Map all model fields to API payload
    Object.keys(model).forEach((field) => {
      const value = (model as any)[field];
      if (typeof value !== "function") {
        // For checkbox fields, ALWAYS include them with explicit true/false values
        if (checkboxFields.includes(field)) {
          // Convert to boolean and always include in payload
          const boolValue = value === true || value === "selected";
          payload[field] = boolValue;
        } else {
          // For non-checkbox fields, include all defined values
          if (value !== undefined) {
            payload[field] = value;
          }
        }
      }
    });

    // Ensure ALL checkbox fields are explicitly set to false if not already in payload
    checkboxFields.forEach((field) => {
      if (!(field in payload)) {
        payload[field] = false;
      }
    });

    return payload;
  }

  /**
   * Save form data to API
   */
  static async saveToApi(
    model: W8EXPFormModel,
    row: any,
    selectedIndex: number = 0
  ): Promise<boolean> {
    try {
      // Enhanced authentication with multiple fallbacks
      let userData;
      let token;

      try {
        userData = getUserIdFromLocalStorage();
        token = userData?.token;
      } catch (error) {
        console.log("W8EXP Auth - getUserIdFromLocalStorage failed:", error);
      }

      // Fallback authentication methods
      if (!token) {
        try {
          const storedUserData = localStorage.getItem("userData");
          if (storedUserData) {
            const parsed = JSON.parse(storedUserData);
            token = parsed.token;
          }
        } catch (error) {
          console.log("W8EXP Auth - fallback userData parsing failed:", error);
        }
      }

      if (!token) {
        try {
          token = localStorage.getItem("token");
        } catch (error) {
          console.log("W8EXP Auth - direct token retrieval failed:", error);
        }
      }

      if (!token) {
        console.error("W8EXP Auth - No authentication token found after all fallbacks");
        throw new Error("No authentication token found");
      }

      // Convert model to API payload
      const formData = this.toApiPayload(model);

      delete formData.giinValidationMessage;
      delete formData.tinValidationMessage;
      formData["Entity Type"] = "";
      formData["Chapter4Status"] = "";

      let parsedFormsData;
      try {
        // Parse formsData and check if it's complex structure or simple array
        parsedFormsData = JSON.parse(row.formsData || "[]");

        // Check if it's the complex structure with formsData property and withholdingData
        if (
          parsedFormsData &&
          typeof parsedFormsData === "object" &&
          parsedFormsData.formsData &&
          Array.isArray(parsedFormsData.formsData)
        ) {
          // Find and update the W8EXP form in the complex structure
          let formUpdated = false;

          for (let i = 0; i < parsedFormsData.formsData.length; i++) {
            const formItem = parsedFormsData.formsData[i];
            if (formItem && formItem.data && Array.isArray(formItem.data)) {
              for (let j = 0; j < formItem.data.length; j++) {
                const dataItem = formItem.data[j];
                if (
                  dataItem &&
                  (dataItem.formType === "W8EXP" || dataItem.Form_number === "W-8EXP")
                ) {
                  parsedFormsData.formsData[i].data[j] = {
                    ...dataItem,
                    ...formData,
                  };
                  formUpdated = true;
                  break;
                }
              }
              if (formUpdated) break;
            }
          }

          if (!formUpdated) {
            parsedFormsData.formsData.push({
              request_id: "new-w8exp-" + Date.now(),
              status: "success",
              data: [formData],
              reportdata: {
                requestId: "new-w8exp-" + Date.now(),
                subRequestId: "new-w8exp-" + Date.now(),
                taxForms: "W8EXP",
                type: "extraction",
              },
            });
          }
        } else {
          // Handle as simple array

          const formsDataArray = Array.isArray(parsedFormsData) ? parsedFormsData : [];

          // Update the form at the specified index (or add if it doesn't exist)

          const currentItem = formsDataArray[selectedIndex];

          if (selectedIndex < formsDataArray.length && currentItem) {
            console.log("📝 Updating existing form at index", selectedIndex);
            formsDataArray[selectedIndex] = {
              ...currentItem,
              ...formData,
            };
          } else {
            console.log("📝 Adding new form at index", selectedIndex);
            while (formsDataArray.length <= selectedIndex) {
              formsDataArray.push({});
            }
            formsDataArray[selectedIndex] = formData;
          }

          parsedFormsData = formsDataArray;
        }
      } catch (error) {
        console.log("W8EXP: Error parsing existing formsData, creating new array:", error);
        parsedFormsData = [formData];
      }

      // Store back as stringified structure (preserving complex format if applicable)
      row.formsData = JSON.stringify(parsedFormsData);

      // Send to API using centralized service
      await updateFileData(row);

      console.log("W8EXP Save operation completed successfully");
      return true;
    } catch (error) {
      console.error("Failed to save W-8EXP form:", error);
      throw error;
    }
  }
}
